import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { UserPlus, Search, KeyRound, Power, Shield, GraduationCap, Check, X, RefreshCw } from 'lucide-react';

const ROLE_LABELS = {
  full: 'Acceso completo',
  limited: 'Acceso limitado a curso intensivo',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const UserAccessAdmin = ({ apiClient }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', password: '', role: 'limited' });
  const [resetId, setResetId] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [confirmToggleId, setConfirmToggleId] = useState(null);

  const fetchUsers = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/users', { params: q ? { q } : {} });
      setUsers(res.data || []);
    } catch (err) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(query.trim());
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.password.trim()) {
      toast.error('Completa nombre, correo y contraseña');
      return;
    }
    setCreating(true);
    try {
      await apiClient.post('/admin/users', {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
      });
      toast.success('Usuario creado');
      setForm({ full_name: '', email: '', password: '', role: 'limited' });
      fetchUsers(query.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setCreating(false);
    }
  };

  const handleChangeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await apiClient.patch(`/admin/users/${u.id}/role`, { role });
      toast.success(`Rol actualizado a "${ROLE_LABELS[role]}"`);
      fetchUsers(query.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar rol');
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await apiClient.patch(`/admin/users/${u.id}/active`, { is_active: !u.is_active });
      toast.success(!u.is_active ? 'Usuario activado' : 'Usuario desactivado');
      setConfirmToggleId(null);
      fetchUsers(query.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar estado');
    }
  };

  const handleResetPassword = async (u) => {
    if (!resetPwd || resetPwd.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }
    try {
      await apiClient.post(`/admin/users/${u.id}/reset-password`, { password: resetPwd });
      toast.success('Contraseña restablecida');
      setResetId(null);
      setResetPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restablecer contraseña');
    }
  };

  return (
    <div className="space-y-6" data-testid="user-access-admin">
      {/* Create user form */}
      <div className="border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-black uppercase tracking-wider mb-4 flex items-center gap-2">
          <UserPlus size={16} /> Registrar nuevo usuario
        </h3>
        <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input
            type="text" placeholder="Nombre completo" value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black"
            data-testid="create-user-name"
          />
          <input
            type="email" placeholder="Correo electrónico" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black"
            data-testid="create-user-email"
          />
          <input
            type="text" placeholder="Contraseña temporal" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black"
            data-testid="create-user-password"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-black bg-white"
            data-testid="create-user-role"
          >
            <option value="limited">Acceso limitado a curso intensivo</option>
            <option value="full">Acceso completo</option>
          </select>
          <button
            type="submit" disabled={creating}
            className="bg-black text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-gray-800 transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5"
            data-testid="create-user-submit"
          >
            {creating ? <RefreshCw size={14} className="animate-spin" /> : <UserPlus size={14} />}
            {creating ? 'Creando...' : 'Crear'}
          </button>
        </form>
        <p className="text-xs text-gray-400 mt-3">
          Acceso completo dura 6 meses · Acceso limitado (curso intensivo) dura 1 mes y 5 días (se cancela solo al vencer).
        </p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" placeholder="Buscar por nombre o correo" value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-black"
            data-testid="user-search-input"
          />
        </div>
        <button type="submit" className="bg-black text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-gray-800" data-testid="user-search-btn">
          Buscar
        </button>
        {query && (
          <button type="button" onClick={() => { setQuery(''); fetchUsers(); }} className="border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-50">
            Limpiar
          </button>
        )}
      </form>

      {/* Users list */}
      {loading ? (
        <div className="py-12 flex justify-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <p className="text-center text-gray-400 py-12">No hay usuarios que coincidan.</p>
      ) : (
        <div className="space-y-3" data-testid="user-access-list">
          {users.map((u) => (
            <div key={u.id} className="border border-gray-200 rounded-xl p-4" data-testid={`user-row-${u.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-black">{u.full_name}</span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${u.role === 'limited' ? 'bg-amber-100 text-amber-700' : 'bg-gray-900 text-white'}`}>
                      {u.role === 'limited' ? <GraduationCap size={11} /> : <Shield size={11} />}
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                    {!u.is_active && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Desactivado</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{u.email}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-[11px] text-gray-400">
                    <span>Registro: {fmtDate(u.created_at)}</span>
                    <span>Último acceso: {fmtDate(u.last_login)}</span>
                    <span>Vence: {fmtDate(u.subscription_expires)}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                <select
                  value={u.role}
                  onChange={(e) => handleChangeRole(u, e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-black bg-white"
                  data-testid={`user-role-select-${u.id}`}
                >
                  <option value="limited">Acceso limitado a curso intensivo</option>
                  <option value="full">Acceso completo</option>
                </select>

                {confirmToggleId === u.id ? (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600">{u.is_active ? '¿Desactivar?' : '¿Activar?'}</span>
                    <button onClick={() => handleToggleActive(u)} className="p-1.5 bg-black text-white rounded-lg" data-testid={`user-toggle-confirm-${u.id}`}><Check size={13} /></button>
                    <button onClick={() => setConfirmToggleId(null)} className="p-1.5 border border-gray-200 rounded-lg" data-testid={`user-toggle-cancel-${u.id}`}><X size={13} /></button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmToggleId(u.id)}
                    className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border ${u.is_active ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-700 hover:bg-green-50'}`}
                    data-testid={`user-toggle-${u.id}`}
                  >
                    <Power size={13} /> {u.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                )}

                {resetId === u.id ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text" placeholder="Nueva contraseña" value={resetPwd}
                      onChange={(e) => setResetPwd(e.target.value)}
                      className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs w-36 focus:outline-none focus:border-black"
                      data-testid={`user-reset-input-${u.id}`}
                    />
                    <button onClick={() => handleResetPassword(u)} className="p-1.5 bg-black text-white rounded-lg" data-testid={`user-reset-confirm-${u.id}`}><Check size={13} /></button>
                    <button onClick={() => { setResetId(null); setResetPwd(''); }} className="p-1.5 border border-gray-200 rounded-lg"><X size={13} /></button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setResetId(u.id); setResetPwd(''); }}
                    className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                    data-testid={`user-reset-${u.id}`}
                  >
                    <KeyRound size={13} /> Contraseña
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserAccessAdmin;
